/**
 * Browser side of an OTA update, framework-free:
 *
 * - `waitForRestart`: waits for the NEW container, not merely for an answer — during a
 *   redeploy the old one keeps serving until the last moment, and reloading too early brings
 *   back the previous release, index.html included.
 * - `reloadOntoNewVersion`: drops the origin cache (`Clear-Site-Data` via the host's
 *   flush endpoint), Cache Storage and service workers, then reloads.
 * - `ensureFreshClient`: reloads when the page is older than the server it talks to, at most
 *   a handful of times (a reload loop is worse than a stale page).
 * - `parseReleaseNotes`: the CHANGELOG section of one version, for the release-notes modal.
 * - `readUpdateNotice` / `watchUpdateNotice`: the update the host is about to apply, read
 *   from `/health` (`update` field), so every page can count down and then wait.
 */
import type { UpdateNotice } from "../core/types";

const PROBE_TIMEOUT_MS = 2_500;

export const normalizeVersion = (v: string | null | undefined): string =>
  (v ?? "").trim().replace(/^[vV]/, "");

async function fetchWithTimeout(
  input: string,
  init: RequestInit = {},
  timeoutMs = PROBE_TIMEOUT_MS,
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export type BrowserUpdateOptions = {
  /** Endpoint answering `{ version }` with no cache, e.g. `/api/status`. */
  healthUrl: string;
  /** JSON key holding the version in the health payload. Default "version". */
  versionKey?: string;
  /** Endpoint answering with `Clear-Site-Data: "cache"` (optional but recommended). */
  flushCacheUrl?: string;
  /** Query parameter appended on reload so proxies do not serve the old index. */
  reloadParam?: string;
  /** localStorage / sessionStorage key prefix. Default "watchpod". */
  storagePrefix?: string;
};

export async function runningVersion(opts: BrowserUpdateOptions): Promise<string | null> {
  try {
    const sep = opts.healthUrl.includes("?") ? "&" : "?";
    const res = await fetchWithTimeout(`${opts.healthUrl}${sep}t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Record<string, unknown>;
    const v = body[opts.versionKey ?? "version"];
    return typeof v === "string" && v.trim() ? v.trim() : null;
  } catch {
    return null; // expected while the old container stops or the new one starts
  }
}

const isNotice = (value: unknown): value is UpdateNotice => {
  const v = value as Partial<UpdateNotice> | null;
  return (
    !!v &&
    typeof v.version === "string" &&
    typeof v.at === "string" &&
    (v.phase === "scheduled" || v.phase === "installing")
  );
};

let updating = false;

/**
 * Whether this page knows an update is in progress: the host announced the install, or
 * the page is waiting for the server to come back on the new version. Until the reload it
 * stays true. Hosts use it to switch automatic error reports off — a failed request while
 * the server restarts on purpose is expected, not a fault.
 */
export const updateInProgress = (): boolean => updating;

/** Marks (or clears) an update in progress by hand, e.g. right after pressing «Aggiorna». */
export const markUpdateInProgress = (value = true): void => {
  updating = value;
};

/**
 * The update announced by the host (`/health` → `update`), or `null` when nothing is due.
 * `undefined` means the server did not answer — while it restarts that is the expected
 * state, and the caller keeps what it had.
 */
export async function readUpdateNotice(
  opts: BrowserUpdateOptions,
): Promise<UpdateNotice | null | undefined> {
  try {
    const sep = opts.healthUrl.includes("?") ? "&" : "?";
    const res = await fetchWithTimeout(`${opts.healthUrl}${sep}t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" },
    });
    if (!res.ok) return undefined;
    const body = (await res.json()) as Record<string, unknown>;
    const notice = isNotice(body.update) ? body.update : null;
    if (notice?.phase === "installing") updating = true;
    return notice;
  } catch {
    return undefined;
  }
}

/**
 * Reads the announced update every `intervalMs` (default 20 s) and reports every change.
 * Returns the function that stops it. Unreachable answers are skipped: a restarting
 * server does not cancel the update screen.
 */
export function watchUpdateNotice(
  opts: BrowserUpdateOptions,
  onNotice: (notice: UpdateNotice | null) => void,
  intervalMs = 5_000,
): () => void {
  let stopped = false;
  let last = "";
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tick = async () => {
    const notice = await readUpdateNotice(opts);
    if (stopped) return;
    if (notice !== undefined) {
      const key = JSON.stringify(notice);
      if (key !== last) {
        last = key;
        onNotice(notice);
      }
    }
    timer = setTimeout(() => void tick(), intervalMs);
  };
  void tick();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

/** Resolves true once the server reports `expectedVersion` (or any version ≠ `before`). */
export async function waitForRestart(
  opts: BrowserUpdateOptions,
  expectedVersion: string,
  before: string,
  onTick: (seconds: number) => void,
  timeoutMs = 5 * 60_000,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  const startedAt = Date.now();
  const expected = normalizeVersion(expectedVersion);
  updating = true;
  while (Date.now() < deadline) {
    onTick(Math.round((Date.now() - startedAt) / 1000));
    const version = await runningVersion(opts);
    if (
      version &&
      (expected
        ? normalizeVersion(version) === expected
        : normalizeVersion(version) !== normalizeVersion(before))
    ) {
      return true;
    }
    await new Promise((r) => setTimeout(r, 2_000));
  }
  // Never came back: whatever fails from now on is worth reporting again.
  updating = false;
  return false;
}

export async function reloadOntoNewVersion(opts: BrowserUpdateOptions): Promise<void> {
  if (opts.flushCacheUrl) {
    try {
      await fetchWithTimeout(`${opts.flushCacheUrl}?t=${Date.now()}`, { cache: "no-store" }, 3_000);
    } catch {
      // mid-restart: reload anyway
    }
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // nothing cached
  }
  try {
    const workers = await navigator.serviceWorker?.getRegistrations?.();
    await Promise.all((workers ?? []).map((w) => w.unregister()));
  } catch {
    // no service worker
  }
  const target = new URL(window.location.href);
  target.searchParams.set(opts.reloadParam ?? "_reload", Date.now().toString());
  window.location.replace(target.toString());
}

const MAX_STALE_RELOADS = 5;

/**
 * Compares the bundle's version with the server's; reloads when older. Returns the server's
 * version when reloading did not fix it — the host should say so out loud.
 */
export async function ensureFreshClient(
  opts: BrowserUpdateOptions,
  appVersion: string,
): Promise<string | null> {
  const key = `${opts.storagePrefix ?? "watchpod"}.stale-reloads`;
  let attempts = 0;
  try {
    attempts = Number(sessionStorage.getItem(key) ?? "0");
  } catch {
    // private browsing
  }
  try {
    const version = await runningVersion(opts);
    if (!version || normalizeVersion(version) === normalizeVersion(appVersion)) {
      try {
        sessionStorage.removeItem(key);
        const url = new URL(window.location.href);
        const param = opts.reloadParam ?? "_reload";
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          window.history.replaceState(null, "", url.toString());
        }
      } catch {
        // nothing to clean
      }
      return null;
    }
    if (attempts >= MAX_STALE_RELOADS) return version;
    try {
      sessionStorage.setItem(key, String(attempts + 1));
    } catch {
      if (attempts > 0) return version;
    }
    setTimeout(() => void reloadOntoNewVersion(opts), 100);
    return null;
  } catch {
    return null;
  }
}

// ---- prompt bookkeeping -----------------------------------------------------------------------

/** The update prompt is shown once per version; afterwards it waits in the settings card. */
export const alreadyPrompted = (version: string, prefix = "watchpod"): boolean => {
  try {
    return localStorage.getItem(`${prefix}.update.prompted`) === version;
  } catch {
    return false;
  }
};
export const markPrompted = (version: string, prefix = "watchpod"): void => {
  try {
    localStorage.setItem(`${prefix}.update.prompted`, version);
  } catch {
    // private browsing
  }
};
/** Release notes open once after an update: the first load of a version not yet seen. */
export const shouldShowReleaseNotes = (appVersion: string, prefix = "watchpod"): boolean => {
  try {
    return localStorage.getItem(`${prefix}.release-notes.seen`) !== appVersion;
  } catch {
    return false;
  }
};
export const markReleaseNotesSeen = (appVersion: string, prefix = "watchpod"): void => {
  try {
    localStorage.setItem(`${prefix}.release-notes.seen`, appVersion);
  } catch {
    // private browsing
  }
};

// ---- CHANGELOG parsing ------------------------------------------------------------------------

export type ReleaseNotesGroup = {
  title: string;
  items: string[];
  /** Nesting of each item (0 top level, 1 sub-item); absent when all are top level. */
  levels?: number[];
};
export type ReleaseNotes = { version: string; date?: string; groups: ReleaseNotesGroup[] };

/**
 * Extracts one version's section from a CHANGELOG.md. Accepts both heading styles used in
 * the fleet: `## [1.2.3] - 2026-08-19` (Keep a Changelog) and `## 1.2.3 — 2026-08-29` (memo).
 * Pre-releases (`## [0.5.0-rc.18] - …`) count as versions. Groups are `### Title`; items are `- text`. Only THIS version's entry, never the newest
 * as a stand-in: a window titled with a version that is not the one running says the
 * update did not happen.
 */
/**
 * The groups of a changelog body (`### Title` + `- item`), without the version heading: the
 * same reading for a CHANGELOG section and for a release's notes. Indented bullets are
 * sub-items (`levels[i] === 1`); free prose becomes an item of its own.
 */
export function parseNotesBody(body: string): ReleaseNotesGroup[] {
  const groups: ReleaseNotesGroup[] = [];
  let current: ReleaseNotesGroup | undefined;
  let paragraph: string[] = [];
  // After a blank line, indented text starts a paragraph of its own instead of continuing
  // the bullet above it.
  let afterBlank = false;
  const flushParagraph = () => {
    if (paragraph.length) {
      if (!current) {
        current = { title: "Novità", items: [] };
        groups.push(current);
      }
      current.items.push(paragraph.join(" "));
      if (current.levels) current.levels.push(0);
      paragraph = [];
    }
  };
  for (const raw of body.split("\n")) {
    const line = raw.trimEnd();
    const blankBefore = afterBlank;
    afterBlank = line.trim() === "";
    const group = /^###\s+(.+?)\s*$/.exec(line);
    if (group) {
      flushParagraph();
      current = { title: (group[1] ?? "").replace(/^(Fix|Feature)\s+—\s+/i, "$1 — "), items: [] };
      groups.push(current);
      continue;
    }
    const item = /^(\s*)[-*]\s+(.+?)\s*$/.exec(line);
    if (item) {
      flushParagraph();
      if (!current) {
        current = { title: "Novità", items: [] };
        groups.push(current);
      }
      // An indented bullet is a sub-item: its own line, one level in — not glued to the
      // line above.
      const level = (item[1] ?? "").length >= 2 ? 1 : 0;
      current.items.push(item[2] ?? "");
      if (level || current.levels) {
        current.levels = [...(current.levels ?? current.items.slice(0, -1).map(() => 0)), level];
      }
      continue;
    }
    // Continuation of a wrapped bullet (indented) → append to the last item.
    if (/^\s{2,}\S/.test(raw) && current?.items.length && !paragraph.length && !blankBefore) {
      current.items[current.items.length - 1] =
        `${current.items[current.items.length - 1] ?? ""} ${line.trim()}`;
      continue;
    }
    if (line.trim() === "") {
      flushParagraph();
      continue;
    }
    paragraph.push(line.trim()); // free prose under a group (memo's changelog style)
  }
  flushParagraph();
  return groups.filter((g) => g.items.length > 0);
}

export function parseReleaseNotes(markdown: string, appVersion: string): ReleaseNotes {
  // A pre-release (`0.5.0-rc.18`) is a version like any other: without it the heading was
  // read as prose, the section fell back to the empty `[Unreleased]`, and the window said
  // nothing about the update that had just happened.
  const heading =
    /^## \[?(v?\d+\.\d+\.\d+(?:-[0-9A-Za-z.]+)?|Unreleased)\]?(?:\s*[-—–]\s*(.+?))?\s*$/gim;
  const sections: (ReleaseNotes & { at: number; start: number })[] = [];
  let m: RegExpExecArray | null;
  while ((m = heading.exec(markdown))) {
    // `m[1]` c'e' sempre — il gruppo non e' opzionale — ma un host con
    // `noUncheckedIndexedAccess` non lo sa, e non deve doverci credere sulla parola.
    const found = m[1] ?? "";
    sections.push({
      version: normalizeVersion(found) || found,
      date: m[2],
      groups: [],
      // Dove comincia il corpo, e dove comincia **l'intestazione**: servono tutte e due.
      // Il corpo di una sezione finisce dove comincia l'intestazione della prossima, non
      // dove finisce: tagliando a `start` la riga `## 1.2.3 — data` della successiva
      // restava dentro, e compariva in fondo alle note come se fosse una voce.
      at: m.index,
      start: heading.lastIndex,
    });
  }
  const wanted = normalizeVersion(appVersion);
  const selected =
    sections.find((s) => s.version === wanted) ?? sections.find((s) => s.version === "Unreleased");
  if (!selected) return { version: wanted, groups: [] };
  const idx = sections.indexOf(selected);
  const body = markdown.slice(selected.start, sections[idx + 1]?.at ?? markdown.length);
  const groups = parseNotesBody(body);
  return {
    version: selected.version === "Unreleased" ? wanted : selected.version,
    date: selected.date,
    groups,
  };
}
